import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { QrCode, ArrowRight } from "@phosphor-icons/react";

/**
 * Landing for QR scans. URL: /scan?area_id=XXXX
 *
 * Flow:
 *  - If not authenticated, AuthGate already redirects to /login (we then come back).
 *  - Find user's open task for that area (pending/in_progress).
 *  - If none, show the available checklists for that area so user can self-add.
 */
export default function ScanArea() {
  const [params] = useSearchParams();
  const areaId = params.get("area_id");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [area, setArea] = useState(null);
  const [task, setTask] = useState(null);
  const [checklists, setChecklists] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!areaId) {
      setErr("Missing area_id in QR link.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        // Find area
        const { data: areas } = await api.get("/masters/areas");
        const a = areas.find((x) => x.id === areaId);
        if (!a) {
          setErr("Area not found or you don't have access to it.");
          setLoading(false);
          return;
        }
        setArea(a);
        // Try to find user's open task
        const { data: tasks } = await api.get("/tasks", { params: { mine: true, area_id: areaId } });
        const open = tasks.find((t) => t.status === "pending" || t.status === "in_progress");
        if (open) {
          setTask(open);
          // If pending → start; otherwise just go there
          if (open.status === "pending") {
            try { await api.post(`/tasks/${open.id}/start`); } catch (_) {}
          }
          navigate(`/task/${open.id}`, { replace: true });
          return;
        }
        // No open task — show available checklists for this area
        const { data: avail } = await api.get("/checklists/available");
        setChecklists(avail.filter((c) => c.area_id === areaId));
        setLoading(false);
      } catch (e) {
        setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load.");
        setLoading(false);
      }
    })();
  }, [areaId, navigate]);

  const pick = async (cid) => {
    try {
      const { data } = await api.post("/tasks", { checklist_id: cid, source: "manual" });
      await api.post(`/tasks/${data.id}/start`);
      navigate(`/task/${data.id}`, { replace: true });
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-xl space-y-5" data-testid="scan-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center"><QrCode size={20} weight="fill" className="text-white" /></div>
        <div>
          <div className="label-small text-blue-700">QR scan</div>
          <h1 className="text-2xl font-extrabold text-slate-900 mt-0.5" style={{ fontFamily: "Manrope" }}>
            {area ? area.name : "Loading…"}
          </h1>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500">Looking up area…</div>}
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

      {!loading && !err && area && checklists.length > 0 && (
        <div className="flat-card p-5">
          <div className="label-small mb-3">Pick a checklist to begin</div>
          <ul className="space-y-2">
            {checklists.map((c) => (
              <li key={c.id}>
                <button data-testid={`scan-pick-${c.id}`} onClick={() => pick(c.id)} className="w-full text-left border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 rounded-md px-3 py-2.5 transition-colors flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500">{c.frequency} · TAT {c.tat_minutes ?? "—"} min</div>
                  </div>
                  <ArrowRight size={14} className="text-blue-600" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {!loading && !err && area && checklists.length === 0 && (
        <div className="flat-card p-5 text-sm text-slate-600">
          No checklists configured for this area in your scope.
        </div>
      )}
    </div>
  );
}
